import { Dropdown, Image, Menu, Table, TableColumnsType } from "antd";
import NextIcon from "components/Icons/NextIcon";
import PreviousIcon from "components/Icons/PreviousIcon";
import CountUp from "react-countup";
import moment from "moment";
import WalletStatus from "pages/DriverWalletPages/DriverWalletDetail/components/WalletStatus";
import useAccountFinance from "hooks/accounts/useAccountFinance";
import { IAccountFinance } from "models/account";
import { useMemo } from "react";
import { IDriver } from "models/driver";
import { Link } from "react-router-dom";
import { RouteName } from "constants/route";
import ViewIcon from "components/Icons/ViewIcon";
import DateRangeModal from "./components/DateRangeModal";

export type IAccountFinanceFilter =
  | "Today"
  | "Yesterday"
  | "This Week"
  | "Last Week"
  | "This Month"
  | "Last Month"
  | "This Year"
  | "Last Year"
  | "Custom";

interface AccountFinanceFilterButtonProps {
  handleFilterChange: (value: IAccountFinanceFilter) => void;
  value: IAccountFinanceFilter;
}

export function AccountFinanceFilterButton({
  handleFilterChange,
  value,
}: AccountFinanceFilterButtonProps) {
  const handleOnClick = (newValue: IAccountFinanceFilter) => {
    handleFilterChange(newValue);
  };

  const items = useMemo(() => {
    return [
      {
        key: "Today",
        onClick: () => handleOnClick("Today"),
        label: <Label text={"Today"} />,
      },
      {
        key: "Yesterday",
        onClick: () => handleOnClick("Yesterday"),
        label: <Label text={"Yesterday"} />,
      },
      {
        key: "This Week",
        onClick: () => handleOnClick("This Week"),
        label: <Label text={"This Week"} />,
      },
      {
        key: "Last Week",
        onClick: () => handleOnClick("Last Week"),
        label: <Label text={"Last Week"} />,
      },
      {
        key: "This Month",
        onClick: () => handleOnClick("This Month"),
        label: <Label text={"This Month"} />,
      },
      {
        key: "Last Month",
        onClick: () => handleOnClick("Last Month"),
        label: <Label text={"Last Month"} />,
      },
      {
        key: "This Year",
        onClick: () => handleOnClick("This Year"),
        label: <Label text={"This Year"} />,
      },
      {
        key: "Last Year",
        onClick: () => handleOnClick("Last Year"),
        label: <Label text={"Last Year"} />,
      },
      {
        key: "Custom",
        onClick: () => handleOnClick("Custom"),
        label: <Label text={"Custom"} />,
      },
    ];
  }, [handleFilterChange]); // eslint-disable-line

  return (
    <Dropdown overlay={<Menu items={items} />}>
      <button className="outline-none border-none text-dark-blue text-sm flex gap-2 items-center capitalize whitespace-nowrap">
        {value}
        <ArrowDownIcon />
      </button>
    </Dropdown>
  );
}

function Label({ text }: { text: string }) {
  return (
    <span className="text-dark-gray text-sm py-1 px-1 font-medium capitalize">
      {text}
    </span>
  );
}

function ArrowDownIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 15L7 10H17L12 15Z" fill="currentColor" />
    </svg>
  );
}

export default function AccountFinance() {
  const {
    data: { data, loading, page, per_page, total, total_deduct, total_pop_up },
    handleCloseCustomDatePicker,
    showCustomDatePicker,
    dateFilter,
    handlePageChange,
    handleSetDate,
    handleFilterChange,
  } = useAccountFinance();

  return (
    <div className="w-full">
      <div className="w-full px-6 flex flex-wrap-reverse gap-3">
        <div className="flex flex-col px-4 py-6 bg-white min-w-52 max-w-60 rounded-lg gap-y-5 w-full shadow-md">
          <div className="flex flex-col gap-y-4">
            <p className="text-base font-normal text-dark-gray whitespace-nowrap">
              Total Deposit
            </p>
            <h3 className="text-darker-gray font-semibold text-2xl">
              <CountUp end={total_pop_up} />
            </h3>
          </div>
        </div>
        <div className="flex flex-col px-4 py-6 bg-white min-w-52 max-w-60 rounded-lg gap-y-5 w-full shadow-md">
          <div className="flex flex-col gap-y-4">
            <p className="text-base font-normal text-dark-gray whitespace-nowrap">
              Total Withdraw
            </p>
            <h3 className="text-darker-gray font-semibold text-2xl">
              <CountUp end={total_deduct} />
            </h3>
          </div>
        </div>
        <div className="flex flex-col px-4 py-6 bg-white min-w-52 max-w-60 rounded-lg gap-y-5 w-full shadow-md">
          <div className="flex flex-col gap-y-4">
            <p className="text-base font-normal text-dark-gray whitespace-nowrap">
              Total Transaction
            </p>
            <h3 className="text-darker-gray font-semibold text-2xl">
              <CountUp end={total_pop_up - total_deduct} />
            </h3>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end pt-10 pr-6">
        <AccountFinanceFilterButton
          handleFilterChange={handleFilterChange}
          value={
            dateFilter.filter === "Custom" &&
            dateFilter.startDate &&
            dateFilter.endDate
              ? (`${moment(dateFilter.startDate).format("ll")} - ${moment(
                  dateFilter.endDate
                ).format("ll")}` as "Custom")
              : dateFilter.filter === "Custom"
              ? "Custom"
              : dateFilter.filter
          }
        />
      </div>
      <div className="w-full mt-8 px-4">
        <Table
          loading={loading}
          columns={columns}
          dataSource={data}
          onChange={handlePageChange}
          pagination={{
            total,
            pageSize: per_page,
            current: page,
            nextIcon: (
              <span className="flex items-center justify-center w-8 h-8 text-2xl">
                <NextIcon />
              </span>
            ),
            prevIcon: (
              <span className="flex items-center justify-center w-8 h-8 text-2xl">
                <PreviousIcon />
              </span>
            ),
          }}
        />
      </div>
      <DateRangeModal
        handleSetDate={handleSetDate}
        open={showCustomDatePicker}
        handleClose={handleCloseCustomDatePicker}
      />
    </div>
  );
}

const columns: TableColumnsType<IAccountFinance> = [
  {
    title: "Amount",
    dataIndex: "amount",
    render: (value: number) => <p>{value.toFixed(2)}</p>,
  },
  {
    title: "Status",
    dataIndex: "type",
    render: WalletStatus,
  },

  {
    title: "Driver Image",
    dataIndex: "driver",
    render: (driver: IDriver) => (
      <Image
        height={60}
        width={60}
        className="rounded object-cover"
        src={driver.image}
        preview={false}
      />
    ),
  },
  {
    title: "Driver Name",
    dataIndex: "driver",
    render: (driver: IDriver) => (
      <p>{`${driver.first_name} ${driver.last_name}`}</p>
    ),
  },
  {
    title: "Date",
    dataIndex: "created_at",
    render: (value: string) => <p>{moment(value).format("ll")}</p>,
  },
  {
    title: "View Driver",
    dataIndex: "driver",
    render: (value: IDriver) => (
      <div className="flex items-center gap-x-3 pl-8">
        <Link
          to={RouteName.DRIVER_DETAIL.replace(":id", value.id)}
          className="rounded-ful hover:text-black"
        >
          <ViewIcon />
        </Link>
      </div>
    ),
  },
];
